import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Filter, X, ShieldCheck, FolderOpen, Users, Layers, Gauge } from "lucide-react";
import { cn } from "../../utils/cn";
import { AppFilters } from "../../context";
import { User, Project } from "../../types";
import { UserMultiSelect } from "../ui/UserMultiSelect";

interface FilterPanelProps {
    isOpen: boolean;
    onClose: () => void;
    filters: AppFilters;
    setFilters: React.Dispatch<React.SetStateAction<AppFilters>> | ((filters: Partial<AppFilters>) => void);
    uniqueOwners: string[];
    users?: User[];
    projects?: Project[];
}

export function FilterPanel({ isOpen, onClose, filters, setFilters, uniqueOwners, users = [], projects = [] }: FilterPanelProps) {
    const activeCount = useMemo(() => {
        let count = 0;
        if (filters.lane.length > 0) count++;
        if (filters.owner.length > 0) count++;
        if (filters.arq && filters.arq.length > 0) count++;
        if (filters.sgmOnly) count++;
        if (filters.priority.length > 0) count++;
        if (filters.projectId.length > 0) count++;
        return count;
    }, [filters]);

    const resetFilters = () => {
        (setFilters as any)({ lane: [], owner: [], arq: [], sgmOnly: false, priority: [], projectId: [] });
    };

    const updateFilter = (key: keyof AppFilters, value: any) => {
        if (typeof setFilters === 'function') {
            (setFilters as any)({ [key]: value });
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 400, damping: 30 }}
                            className="w-full max-w-4xl bg-white dark:bg-[#1a1a24] rounded-2xl shadow-2xl overflow-hidden pointer-events-auto border border-siemens-deep-blue/10 dark:border-[#3d3d4d]"
                        >
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-siemens-deep-blue/5 dark:border-[#3d3d4d] flex items-center justify-between bg-gradient-to-r from-siemens-petrol/5 to-transparent dark:from-siemens-petrol/10">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-siemens-petrol/10">
                                        <Filter className="w-5 h-5 text-siemens-petrol" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-siemens-deep-blue dark:text-white">Filter Epics</h3>
                                        <p className="text-xs text-siemens-text-soft">Refine your view by selecting filters</p>
                                    </div>
                                    {activeCount > 0 && (
                                        <span className="bg-siemens-petrol text-white text-xs font-bold px-2 py-1 rounded-full">
                                            {activeCount} active
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    {activeCount > 0 && (
                                        <button
                                            onClick={resetFilters}
                                            className="text-sm font-semibold text-siemens-petrol hover:text-siemens-petrol/80 transition-colors px-3 py-1.5 rounded-lg hover:bg-siemens-petrol/10"
                                        >
                                            Reset All
                                        </button>
                                    )}
                                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors">
                                        <X className="w-5 h-5 text-siemens-text-soft" />
                                    </button>
                                </div>
                            </div>

                            {/* Content - Landscape Grid Layout */}
                            <div className="p-6">
                                <div className="grid grid-cols-3 gap-6">

                                    {/* Column 1: Lane + Priority */}
                                    <div className="space-y-5">
                                        {/* Lane Filter */}
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <Layers className="w-4 h-4 text-siemens-petrol" />
                                                <label className="text-xs font-bold text-siemens-deep-blue dark:text-white uppercase tracking-wider">Kanban Lane</label>
                                            </div>
                                            <div className="grid grid-cols-2 gap-1.5">
                                                {["Now", "Next", "Later", "Unassigned", "Done"].map(option => {
                                                    const isSelected = filters.lane.includes(option);
                                                    return (
                                                        <button
                                                            key={option}
                                                            onClick={() => {
                                                                const newLanes = isSelected
                                                                    ? filters.lane.filter(l => l !== option)
                                                                    : [...filters.lane, option];
                                                                updateFilter('lane', newLanes);
                                                            }}
                                                            className={cn(
                                                                "px-3 py-2 text-xs font-semibold rounded-lg border transition-all",
                                                                isSelected
                                                                    ? "bg-siemens-petrol text-white border-siemens-petrol shadow-sm"
                                                                    : "bg-slate-50 dark:bg-[#13131a] text-siemens-deep-blue dark:text-siemens-text-soft border-siemens-deep-blue/10 dark:border-[#3d3d4d] hover:border-siemens-petrol/50"
                                                            )}
                                                        >
                                                            {option}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {filters.lane.length === 0 && (
                                                <p className="text-[10px] text-siemens-text-soft italic">All lanes</p>
                                            )}
                                        </div>

                                        {/* Priority Filter */}
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <Gauge className="w-4 h-4 text-siemens-petrol" />
                                                <label className="text-xs font-bold text-siemens-deep-blue dark:text-white uppercase tracking-wider">Priority</label>
                                            </div>
                                            <div className="flex gap-1.5">
                                                {["High", "Medium", "Low"].map(option => {
                                                    const isSelected = filters.priority.includes(option);
                                                    const colors = {
                                                        High: isSelected ? "bg-red-500 border-red-500 text-white" : "hover:border-red-300",
                                                        Medium: isSelected ? "bg-amber-500 border-amber-500 text-white" : "hover:border-amber-300",
                                                        Low: isSelected ? "bg-emerald-500 border-emerald-500 text-white" : "hover:border-emerald-300"
                                                    };
                                                    return (
                                                        <button
                                                            key={option}
                                                            onClick={() => {
                                                                const newPriorities = isSelected
                                                                    ? filters.priority.filter(p => p !== option)
                                                                    : [...filters.priority, option];
                                                                updateFilter('priority', newPriorities);
                                                            }}
                                                            className={cn(
                                                                "flex-1 py-2 text-xs font-bold rounded-lg border transition-all",
                                                                isSelected
                                                                    ? colors[option as keyof typeof colors]
                                                                    : `bg-slate-50 dark:bg-[#13131a] text-siemens-deep-blue dark:text-siemens-text-soft border-siemens-deep-blue/10 dark:border-[#3d3d4d] ${colors[option as keyof typeof colors]}`
                                                            )}
                                                        >
                                                            {option}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {filters.priority.length === 0 && (
                                                <p className="text-[10px] text-siemens-text-soft italic">All priorities</p>
                                            )}
                                        </div>

                                        {/* SGM Toggle */}
                                        <div className="flex items-center justify-between p-3 rounded-lg border border-siemens-deep-blue/10 dark:border-[#3d3d4d] bg-slate-50/50 dark:bg-[#13131a]/50">
                                            <div className="flex items-center gap-2">
                                                <ShieldCheck className="w-4 h-4 text-siemens-petrol" />
                                                <span className="text-xs font-semibold text-siemens-deep-blue dark:text-white">SGM Direction</span>
                                            </div>
                                            <button
                                                onClick={() => updateFilter('sgmOnly', !filters.sgmOnly)}
                                                className={cn(
                                                    "w-10 h-5 rounded-full relative transition-colors duration-200",
                                                    filters.sgmOnly ? "bg-siemens-petrol" : "bg-slate-300 dark:bg-slate-600"
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        "absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200",
                                                        filters.sgmOnly ? "translate-x-5" : "translate-x-0"
                                                    )}
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Column 2: Projects (compact chips) */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <FolderOpen className="w-4 h-4 text-siemens-petrol" />
                                            <label className="text-xs font-bold text-siemens-deep-blue dark:text-white uppercase tracking-wider">Projects</label>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {projects.map(p => {
                                                const isSelected = filters.projectId.includes(p.id);
                                                return (
                                                    <button
                                                        key={p.id}
                                                        onClick={() => {
                                                            const newProjects = isSelected
                                                                ? filters.projectId.filter(id => id !== p.id)
                                                                : [...filters.projectId, p.id];
                                                            updateFilter('projectId', newProjects);
                                                        }}
                                                        className={cn(
                                                            "px-2.5 py-1.5 text-[11px] font-medium rounded-full border transition-all whitespace-nowrap",
                                                            isSelected
                                                                ? "bg-siemens-petrol text-white border-siemens-petrol"
                                                                : "bg-slate-50 dark:bg-[#13131a] text-siemens-deep-blue dark:text-siemens-text-soft border-siemens-deep-blue/10 dark:border-[#3d3d4d] hover:border-siemens-petrol/50"
                                                        )}
                                                    >
                                                        {p.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {filters.projectId.length === 0 && (
                                            <p className="text-[10px] text-siemens-text-soft italic">All projects</p>
                                        )}
                                    </div>

                                    {/* Column 3: Owners + Architects */}
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Users className="w-4 h-4 text-siemens-petrol" />
                                            <label className="text-xs font-bold text-siemens-deep-blue dark:text-white uppercase tracking-wider">People</label>
                                        </div>

                                        <UserMultiSelect
                                            label="Owners"
                                            selectedUserIds={filters.owner}
                                            users={users}
                                            onChange={(ids) => updateFilter('owner', ids)}
                                            placeholder="Any owner..."
                                        />

                                        <UserMultiSelect
                                            label="Architects"
                                            selectedUserIds={filters.arq || []}
                                            users={users}
                                            onChange={(ids) => updateFilter('arq', ids)}
                                            placeholder="Any architect..."
                                        />
                                    </div>

                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default FilterPanel;
